import { Box, Stack } from '@mui/material';
import { get } from 'lodash';
import React, { ReactNode, useEffect, useRef, useState } from 'react';

interface UserProps {
	fullWidth?: string;
	columnToDivide?: string;
	[key: string]: any; // Allows additional props to be passed
}

interface FieldGroupProps {
	userProps?: UserProps;
	eachFieldProps?: any;
	children: ReactNode[];
}

const FieldGroup: React.FC<FieldGroupProps> = (props) => {
	let columnToDivide = 2;

	if (get(props, 'userProps.columnToDivide', '') !== '') {
		columnToDivide = parseInt(get(props, 'userProps.columnToDivide', ''), 10);
	} else if (get(props, 'eachFieldProps.fields', []).length === 1) {
		if (get(props, 'eachFieldProps.fields.0.type') === 'textArea' || get(props, 'eachFieldProps.fields.0.type') === 'divider') {
			columnToDivide = 1;
		}
	} else if (get(props, 'userProps.fullWidth', '') !== '') {
		columnToDivide = 1;
	} else if (props.children.length >= 2) {
		columnToDivide = props.children.length;
	}

	// --- Responsive by container width ---
	const stackRef = useRef<HTMLDivElement>(null);
	const [isNarrow, setIsNarrow] = useState(false);

	useEffect(() => {
		const node = stackRef.current;
		if (!node) {
			return;
		}

		const observer = new window.ResizeObserver((entries) => {
			for (const entry of entries) {
				if (entry.contentRect.width <= 400) {
					setIsNarrow(true);
				} else {
					setIsNarrow(false);
				}
			}
		});
		observer.observe(node);

		return () => observer.disconnect();
	}, []);

	const childrenCount = React.Children.count(props.children);

	return (
		<Stack
			ref={stackRef}
			sx={{
				width: '100%',
				alignItems: get(props, 'userProps.alignItems', 'flex-start'),
				columnGap: get(props, 'userProps.columnGap', '2rem'),
			}}
			direction={isNarrow ? 'column' : get(props, 'userProps.direction', 'row')}
			useFlexGap
		>
			{React.Children.map(props.children, (child, index) => {
				const fieldWidth = get(props, `eachFieldProps.fields.${index}.fieldGroupWidth`, null);

				return (
					<Box
						key={index} // Add a key to the mapped children
						sx={{
							width: isNarrow ? '100%' : fieldWidth || `calc(100% / ${columnToDivide})`,
							// Wenn nur ein Kind, dann unten Abstand wie columnGap
							...(childrenCount === 1 && {
								pr: get(props, 'userProps.columnGap'),
							}),
						}}
					>
						{child}
					</Box>
				);
			})}
		</Stack>
	);
};

export default FieldGroup;
